import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useT } from "../i18n";

/** Banner de cookies conforme a RGPD / directiva ePrivacy (criterios AEPD):
 *  - Información clara de qué se almacena y para qué.
 *  - "Aceptar todo" y "Solo esenciales" con la misma prominencia.
 *  - Sin muro de cookies: la web funciona igual se elija lo que se elija.
 *  - Preferencia reconsultable: la política (/cookies) permite cambiarla.
 *
 *  Realidad técnica de HelixScan: solo almacenamiento esencial (sesión, tema,
 *  preferencias) en localStorage; sin cookies de analítica ni publicidad.
 *  El toggle de analítica existe para cumplir si se añade en el futuro:
 *  cualquier herramienta deberá comprobar antes hx_cookie_consent.analytics.
 */

const CONSENT_KEY = "hx_cookie_consent";
const TEAL = "#0891b2";
const TEAL_DARK = "#0a7a96";
const INK = "#0b1215";

export type CookieConsentValue = { essential: true; analytics: boolean; ts: string };

export function getCookieConsent(): CookieConsentValue | null {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    return raw ? (JSON.parse(raw) as CookieConsentValue) : null;
  } catch {
    return null;
  }
}

export function resetCookieConsent() {
  localStorage.removeItem(CONSENT_KEY);
  window.dispatchEvent(new Event("hx-cookie-reset"));
}

export default function CookieConsent() {
  const t = useT();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setVisible(getCookieConsent() === null);
    const onReset = () => setVisible(true);
    window.addEventListener("hx-cookie-reset", onReset);
    return () => window.removeEventListener("hx-cookie-reset", onReset);
  }, []);

  if (!visible) return null;

  const save = (analytics: boolean) => {
    localStorage.setItem(CONSENT_KEY, JSON.stringify({
      essential: true, analytics, ts: new Date().toISOString(),
    } satisfies CookieConsentValue));
    setVisible(false);
  };

  // Botones píldora (mismo tamaño → misma prominencia, criterio AEPD)
  const btn: React.CSSProperties = {
    flex: 1, minWidth: 150, padding: "13px 20px", borderRadius: 999,
    fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
    lineHeight: 1, transition: "transform 0.2s cubic-bezier(.25,.6,.3,1), background 0.2s",
  };

  return (
    <div role="dialog" aria-label={t("cookieConsent.ariaLabel")} aria-live="polite" style={{
      position: "fixed", zIndex: 1000,
      left: "50%", transform: "translateX(-50%)",
      bottom: 16, width: "min(640px, calc(100vw - 24px))",
      color: INK,
      // Liquid glass (mismo material que la landing v5) con fallback sólido
      background: "rgba(255,255,255,0.72)",
      WebkitBackdropFilter: "blur(28px) saturate(1.9)",
      backdropFilter: "blur(28px) saturate(1.9)",
      border: "1px solid rgba(255,255,255,0.85)",
      borderRadius: 26,
      boxShadow: "0 24px 80px -24px rgba(11,40,50,0.35), inset 0 1px 0 rgba(255,255,255,0.95)",
      padding: "22px 24px",
      fontFamily: "'Inter', -apple-system, system-ui, sans-serif",
    }}>
      <div style={{ fontSize: 15, fontWeight: 700, letterSpacing: "-0.01em", marginBottom: 6 }}>
        {t("cookieConsent.title")}
      </div>
      <p style={{ margin: "0 0 16px", fontSize: 13.5, lineHeight: 1.55, color: "rgba(11,18,21,0.68)" }}>
        {t("cookieConsent.bodyPre")}<strong>{t("cookieConsent.bodyEssential")}</strong>{t("cookieConsent.bodyMid")}<strong>{t("cookieConsent.bodyNoAds")}</strong>{t("cookieConsent.bodyPost")}
        <Link to="/cookies" style={{ color: TEAL, fontWeight: 600 }}>{t("cookieConsent.policyLink")}</Link>
      </p>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        <button onClick={() => save(false)}
          onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.02)"; e.currentTarget.style.background = "rgba(255,255,255,0.9)"; }}
          onMouseLeave={e => { e.currentTarget.style.transform = ""; e.currentTarget.style.background = "rgba(255,255,255,0.6)"; }}
          style={{
            ...btn, background: "rgba(255,255,255,0.6)", color: INK,
            border: "1px solid rgba(11,18,21,0.18)",
            boxShadow: "inset 0 1px 0 rgba(255,255,255,0.9)",
          }}>
          {t("cookieConsent.essentialOnly")}
        </button>
        <button onClick={() => save(true)}
          onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.02)"; e.currentTarget.style.background = TEAL_DARK; }}
          onMouseLeave={e => { e.currentTarget.style.transform = ""; e.currentTarget.style.background = TEAL; }}
          style={{
            ...btn, background: TEAL, color: "#fff", border: 0,
            boxShadow: "0 6px 24px -8px rgba(8,145,178,0.5)",
          }}>
          {t("cookieConsent.acceptAll")}
        </button>
      </div>
    </div>
  );
}
